import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import uuid from 'uuid/v1';
import { useDropzone } from 'react-dropzone';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@mui/styles';
import { Typography, Button, Link, List, ListItem, ListItemIcon, ListItemText, colors, Grid } from '@mui/material';

import FileCopyIcon from '@mui/icons-material/FileCopy';

import bytesToSize from 'utils/bytesToSize';

import { SetImage, SetFiles } from '../../../actions/IncidentTrackerActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	dropZone: {
		border: `1px dashed ${theme.palette.divider}`,
		padding: theme.spacing(6),
		outline: 'none',
		display: 'flex',
		justifyContent: 'center',
		flexWrap: 'wrap',
		alignItems: 'center',
		'&:hover': {
			backgroundColor: colors.grey[50],
			opacity: 0.5,
			cursor: 'pointer',
		},
	},
	dragActive: {
		backgroundColor: colors.grey[50],
		opacity: 0.5,
	},
	image: {
		width: 130,
	},
	info: {
		marginTop: theme.spacing(1),
	},
	list: {
		maxHeight: 320,
	},
	actions: {
		marginTop: theme.spacing(2),
		display: 'flex',
		justifyContent: 'flex-end',
		'& > * + *': {
			marginLeft: theme.spacing(2),
		},
	},
}));

const FileDropAndPrintZone = (props) => {
	const { multiple, acceptedTypes, maxSize, className, ...rest } = props;
	const firstDivRef = useRef(null);
	const secondDivRef = useRef(null);
	const dispatch = useDispatch();
	const setImage = image => dispatch(SetImage(image));
	const setFiles = files => dispatch(SetFiles(files));
	const image = useSelector((state) => state.incidentTrackerReducer.image);

	const classes = useStyles();

	const [fileList, setFileList] = useState([]);

	const handleDrop = useCallback((acceptedFiles) => {
		if (multiple) {
			setFileList((f) => [...f].concat(acceptedFiles));
		} else {
			setFileList([...acceptedFiles]);
		}
	}, []);

	const handleRemoveAll = () => {
		setFileList([]);
	};

	useMemo(() => {
		setFiles(fileList);
	}, [fileList]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		multiple,
		accept: acceptedTypes,
		maxSize,
		onDrop: handleDrop,
	});

	useEffect(() => {
		const firstDivHeight = firstDivRef.current.clientHeight;
		if (secondDivRef.current) {
			secondDivRef.current.style.height = `${firstDivHeight}px`;
		}
	}, [firstDivRef.current]);

	const [printImg, setPrintImg] = useState();

	const handleRemoveImage = () => {
		setImage('');
		setPrintImg(null);
		const firstDivHeight = firstDivRef.current.clientHeight;
		if (secondDivRef.current) {
			secondDivRef.current.style.height = `${firstDivHeight}px`;
		}
	};

	const _handleOnPaste = (event) => {
		const { items } = event.clipboardData || event.originalEvent.clipboardData;
		let blob = null;
		for (let i = 0; i < items.length; i++) {
			if (items[i].type.indexOf('image') === 0) {
				blob = items[i].getAsFile();
				setImage(blob);
			}
		}
		if (blob !== null) {
			const reader = new FileReader();
			reader.onload = function load(eventer) {
				setPrintImg(eventer.target.result);
			};
			reader.readAsDataURL(blob);
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid item md={4}>
				<div ref={firstDivRef} {...rest} className={clsx(classes.root, className)} style={{ border: '1px dashed #ccc' }}>
					<div
						className={clsx({
							[classes.dropZone]: true,
							[classes.dragActive]: isDragActive,
						})}
						{...getRootProps()}
					>
						<input {...getInputProps()} />
						<div>
							<img
								alt="Select file"
								className={classes.image}
								src="./images/undraw_add_file2_gvbb.svg"
							/>
						</div>
						<div>
							<Typography gutterBottom variant="h3">
								Select files
							</Typography>
							<Typography
								className={classes.info}
								color="textSecondary"
								variant="body1"
							>
								Drop files here or click
								{' '}
								<Link underline="always">browse</Link>
								{' '}
								thorough your
								machine
							</Typography>
						</div>
					</div>
					{fileList.length > 0 && (
						<>
							<PerfectScrollbar options={{ suppressScrollX: true }}>
								<List className={classes.list}>
									{fileList.map((file, i) => (
										<ListItem
											divider={i < fileList.length - 1}
											key={uuid()}
										>
											<ListItemIcon>
												<FileCopyIcon />
											</ListItemIcon>
											<ListItemText
												primary={file.name}
												primaryTypographyProps={{
													variant: 'h5',
												}}
												secondary={bytesToSize(file.size)}
											/>
										</ListItem>
									))}
								</List>
							</PerfectScrollbar>
							<div className={classes.actions}>
								<Button onClick={handleRemoveAll} size="small">
									Remove
								</Button>
							</div>
						</>
					)}
				</div>
			</Grid>
			<Grid item md={8}>
				<div onPaste={_handleOnPaste}>
					{(printImg === undefined || printImg === null) && (
						<div
							ref={secondDivRef}
							{...rest}
							className={clsx(classes.root, className)}
							style={{ border: '1px dashed #ccc',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center', }}
						>
							<div>
								<img
									alt="Select file"
									className={classes.image}
									src="./images/undraw_add_file2_gvbb.svg"
								/>
							</div>
							<div>
								<Typography gutterBottom variant="h3">
									Paste Image
								</Typography>
								<Typography
									className={classes.info}
									color="textSecondary"
									variant="body1"
								>
									Paste your print screen or some cut image here
								</Typography>
							</div>
						</div>
					)}
					{(printImg !== undefined && printImg !== null) && (
						<div>
							<img
								hidden={printImg === undefined || printImg === null}
								src={printImg}
								alt="pic"
								width="100%"
								height="100%"
								style={{ border: '1px solid #ccc', maxWidth: '100%', maxHeight: '100%' }}
							/>
							<>
								<PerfectScrollbar options={{ suppressScrollX: true }}>
									<List className={classes.list}>
										<ListItem
											divider={0}
											key={0}
										>
											<ListItemIcon>
												<FileCopyIcon />
											</ListItemIcon>
											<ListItemText
												primary={image.name}
												primaryTypographyProps={{
													variant: 'h5',
												}}
												secondary={bytesToSize(image.size)}
											/>
										</ListItem>

									</List>
								</PerfectScrollbar>
								<div className={classes.actions}>
									<Button onClick={handleRemoveImage} size="small">
										Remove Image
									</Button>
								</div>
							</>
						</div>
					)}
				</div>
			</Grid>
		</Grid>
	);
};

FileDropAndPrintZone.propTypes = {
	className: PropTypes.string,
	multiple: PropTypes.bool,
	acceptedTypes: PropTypes.string,
	maxSize: PropTypes.number,
	upload: PropTypes.func
};

export default FileDropAndPrintZone;
